/**
 * DNF filter-group builder — drives BOTH the campaign targeting section and the
 * blast create sheet from one shared chip-editor core.
 *
 * A "builder" is any element carrying `data-filter-builder="<mode>"`:
 *   - mode "campaign" (`#campaign-targeting-section`): per-group HTMX CRUD. The
 *     campaign already has a pk, so editing a chip serializes THAT group and POSTs
 *     it to the group's data-update-url, which re-renders + outerHTML-swaps the
 *     whole section. The server is the source of truth. Groups reorder via
 *     SortableJS; rename/duplicate/delete are one-shot hx-post actions.
 *   - mode "blast" (`#blast-targeting-section`, inside the create form): a brand-new
 *     blast has no pk, so groups can't be persisted per-keystroke. Instead every
 *     group is kept IN MEMORY and the whole set is serialized into ONE hidden JSON
 *     field (`name="dnf_groups"`) on every edit; a `dnfChanged` body event (carrying
 *     the builder root) then marks an already-shown recipient estimate STALE. It never
 *     re-runs the count — that query takes seconds, so only an explicit press of the
 *     sheet's Estimate button may start one. On init the builder RE-HYDRATES the
 *     group UI from that hidden field's value — so a 422 re-render (which echoes the
 *     submitted `dnf_groups` back via `{{ form.data.dnf_groups }}`) never loses the
 *     operator's groups. Add/remove group + add/remove/edit chip are all client-side.
 *   - mode "template" (inside the recurring-template sheet): uses the same in-memory
 *     serialized DNF contract, then persists the rows when the template form submits.
 *
 * Shared core: schema reading, chip building (`buildChip`), the committed-value
 * model, chip-label refresh, portal-aware editor/wrap resolution, per-group
 * serialize.
 *
 * ONE renderer: every chip — server-rendered or client-added — comes from the
 * Django partial `_campaign_filter_chip.html`. Each builder root server-renders
 * one inert `<template data-chip-template="<key>">` per schema field (see
 * `_campaign_filter_chip_templates.html`), rendered with the `__CF__` sentinel in
 * place of the group id. `buildChip()` clones one and `stampCf()` rewrites the
 * sentinel across the whole subtree — it is the ONLY place a chip template is
 * cloned, so the stamp can never be skipped. There is no JS string builder; the
 * two renderers that used to drift (hardcoded `checked` on the bool radios) are
 * collapsed into the partial.
 *
 * Committed-value model: `wrap._cfValue` is the committed value; the editor DOM
 * is a scratch pad. `serializeGroup` reads the MODEL, never the DOM — a campaign
 * POST carries every chip in the group, so a DOM read would let an unsaved edit
 * in a sibling chip ride along on someone else's commit. Canonical shapes:
 * int/choice → string (`''` = unset); bool → `'true'`/`'false'`/`''` (`''` =
 * unset, NOT `'false'` — an unset bool must emit no `af_<key>` at all or every
 * untouched bool chip silently narrows the audience); multichoice → array of
 * strings (`[]` = unset).
 *
 * Explicit commit boundary: the editor footer is `[Remove] [Cancel] [Save]`.
 * NOTHING commits while you type — the chip label is repainted only by
 * `commitChip()` on Save, so a label can never describe a value that was never
 * saved. Save commits the model FIRST, then closes the menu (closing restores a
 * portaled menu, so freezing the model first keeps the close path inert), then
 * pushes the group (campaign POST / blast hidden-field sync). Cancel reverts the
 * editor eagerly and lets SeeMe's `[data-dropdown-close]` do the closing.
 * Escape / click-away have no close event to hook (every path funnels into
 * dropdowns.js's private `closeMenu()`), so discard is handled by inverting it:
 * a `dropdown:open` listener restores the editor from the committed model on the
 * NEXT open. Enter inside a text editor is a Save shortcut.
 *
 * bool and choice are structurally identical for READING — both are "value of the
 * checked `input[type=radio][data-edit]`, or `''`". choice's first row is an
 * explicit `Any` radio with `value=""` (a radio can't be unchecked, so without it
 * a set choice filter could never return to unset). They diverge only in DISPLAY:
 * bool maps `true`/`false` → `Yes`/`No`, choice looks the label up from the schema.
 *
 * Multichoice portaling gotcha: SeeMe portals the `.dropdown-menu-panel` editor to
 * <body> while open, detaching it from its `[data-chip-wrap]`. So DOM ancestry
 * (closest) breaks — the chip resolves its editor (and vice-versa) by the STAMPED
 * data-cf/data-key identity instead. This is why every chip carries data-cf (the
 * group's id) + data-key.
 *
 * All click/input/change handlers are delegated on `document`, resolving the owning
 * builder per-event via `closest('[data-filter-builder]')`, so both modes coexist
 * and survive outerHTML swaps. Campaign POSTs route through a transient hidden HTMX
 * form (native swap + HX-Trigger toasts + spinners); CSRF is injected by csrf.js.
 */
import Sortable from 'sortablejs';

(function () {
    'use strict';

    var BUILDER_SEL = '[data-filter-builder]';
    var GROUP_SEL = '.card[data-update-url], [data-filter-group]';
    var blastGroupSeq = 0;

    function builderOf(el) { return el && el.closest ? el.closest(BUILDER_SEL) : null; }
    function modeOf(root) { return root ? root.getAttribute('data-filter-builder') : null; }
    function groupCardOf(el) { return el.closest(GROUP_SEL); }
    function usesSerializedDnf(root) {
        var mode = modeOf(root);
        return mode === 'blast' || mode === 'template';
    }
    function dnfGroupContainer(root) {
        return root.querySelector('[data-dnf-groups-container]') || root.querySelector('#blast-groups');
    }
    function dnfHiddenField(root) {
        return root.querySelector('[data-dnf-groups-field]') || root.querySelector('#blast-dnf-groups');
    }

    // ── Resolved field schema (server-emitted json_script inside each builder root) ──
    // Cached on the root; a campaign section swap / blast 422 re-render mints a fresh
    // root element with no cache, so it recomputes automatically.
    function schemaOf(root) {
        if (!root) return {};
        if (root._cfSchema) return root._cfSchema;
        var el = root.querySelector('script[type="application/json"][id$="targeting-schema"]');
        var map = {};
        if (el) {
            try { JSON.parse(el.textContent).forEach(function (f) { map[f.key] = f; }); }
            catch (e) { /* leave empty */ }
        }
        root._cfSchema = map;
        return map;
    }

    function commas(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
    function intOp(key) {
        return key.slice(0, 4) === 'min_' ? '≥ ' : (key.slice(0, 4) === 'max_' ? '≤ ' : '');
    }

    // ── Transient-form HTMX POST — campaign only (reuses swap + HX-Trigger toast) ──
    function submitHxForm(url, fields, target) {
        if (!window.htmx || !url || !target) return;
        var form = document.createElement('form');
        form.setAttribute('hx-post', url);
        form.setAttribute('hx-target', target);
        form.setAttribute('hx-swap', 'outerHTML');
        form.style.display = 'none';
        fields.forEach(function (f) {
            var input = document.createElement('input');
            input.type = 'hidden';
            input.name = f[0];
            input.value = f[1];
            form.appendChild(input);
        });
        form.addEventListener('htmx:afterRequest', function () { form.remove(); });
        document.body.appendChild(form);
        window.htmx.process(form);
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }

    // ── Portal-aware resolution ─────────────────────────────────────────────────
    function editorFor(wrap) {
        var editor = wrap.querySelector('[data-editor]');
        if (editor) return editor;                                   // in place (not portaled)
        var cf = wrap.getAttribute('data-cf'), key = wrap.getAttribute('data-key');
        if (!cf || !key) return null;
        return document.querySelector('[data-editor][data-cf="' + cf + '"][data-key="' + key + '"]');
    }
    function wrapFor(el) {
        var wrap = el.closest('[data-chip-wrap]');
        if (wrap) return wrap;                                        // in place
        var editor = el.closest('[data-editor]');
        if (!editor) return null;
        var cf = editor.getAttribute('data-cf'), key = editor.getAttribute('data-key');
        if (!cf || !key) return null;
        return document.querySelector('[data-chip-wrap][data-cf="' + cf + '"][data-key="' + key + '"]');
    }

    // ── Chip markup — clone the server-rendered <template> for this field ──────
    // Rewrite the `__CF__` sentinel across the cloned subtree. Scans every
    // attribute rather than an allowlist: an allowlist rots the moment the partial
    // grows another id-bearing attribute, and a missed `name` means two groups
    // sharing a radio name and silently unchecking each other. `querySelectorAll`
    // excludes the root, so the root is stamped explicitly. setAttribute (not
    // innerHTML surgery) keeps node identity and form state intact.
    function stampCf(node, cf) {
        var all = [node].concat(Array.prototype.slice.call(node.querySelectorAll('*')));
        all.forEach(function (el) {
            Array.prototype.slice.call(el.attributes).forEach(function (attr) {
                if (attr.value.indexOf('__CF__') === -1) return;
                el.setAttribute(attr.name, attr.value.split('__CF__').join(cf));
            });
        });
        return node;
    }

    // The ONLY place a chip template is cloned — keeps stampCf unskippable.
    function buildChip(root, cf, key) {
        if (!root || !key) return null;
        var tmpl = root.querySelector('template[data-chip-template="' + key + '"]');
        if (!tmpl) return null;
        var wrap = tmpl.content.cloneNode(true).querySelector('[data-chip-wrap]');
        if (!wrap) return null;
        return stampCf(wrap, cf);
    }

    // ── Editor <-> canonical value (portal-aware via editorFor) ────────────────
    function readEditor(wrap) {
        var trigger = wrap.querySelector('[data-chip]');
        var editor = editorFor(wrap);
        if (!trigger || !editor) return '';
        var type = trigger.getAttribute('data-type');
        if (type === 'int') {
            var num = editor.querySelector('input[data-edit]');
            return num ? num.value.trim() : '';
        }
        if (type === 'bool' || type === 'choice') {
            // One radio group for both. bool: '' = unset (NOT 'false'); choice:
            // the explicit `Any` row IS value="" , so it reads back as unset too.
            var checked = editor.querySelector('input[type="radio"][data-edit]:checked');
            return checked ? checked.value : '';
        }
        var vals = [];                              // multichoice (partial's {% else %})
        editor.querySelectorAll('input[type="checkbox"][data-edit]:checked').forEach(function (b) {
            vals.push(b.value);
        });
        return vals;
    }

    function writeEditor(wrap, value) {
        var trigger = wrap.querySelector('[data-chip]');
        var editor = editorFor(wrap);
        if (!trigger || !editor) return;
        var type = trigger.getAttribute('data-type');
        if (type === 'int') {
            var num = editor.querySelector('input[data-edit]');
            if (num) num.value = (value == null) ? '' : String(value);
            return;
        }
        if (type === 'bool' || type === 'choice') {
            // '' checks nothing for bool (values are 'true'/'false') and checks
            // choice's `Any` row (value="") — both mean "unset", as intended.
            var v = (value == null) ? '' : String(value);
            editor.querySelectorAll('input[type="radio"][data-edit]').forEach(function (r) {
                r.checked = (r.value === v);
            });
            return;
        }
        var raw = Array.isArray(value) ? value : (value === '' || value == null ? [] : [value]);
        var vals = raw.map(String);                 // multichoice
        editor.querySelectorAll('input[type="checkbox"][data-edit]').forEach(function (cb) {
            cb.checked = vals.indexOf(cb.value) !== -1;
        });
    }

    // ── Committed-value model ─────────────────────────────────────────────────
    // A server-rendered chip that was never opened already carries its committed
    // value in untouched DOM, so seed lazily from the editor on first read.
    function committedValue(wrap) {
        if (wrap._cfValue === undefined) wrap._cfValue = readEditor(wrap);
        return wrap._cfValue;
    }
    function commitChip(wrap) {
        wrap._cfValue = readEditor(wrap);
        refreshChipLabel(wrap);
    }
    // Discard uncommitted editor state — Cancel calls it eagerly, and the
    // `dropdown:open` restore is the safety net for Escape / click-away.
    function revertChip(wrap) {
        writeEditor(wrap, committedValue(wrap));
    }

    // ── Serialize every chip in a group into af_<key> [name, value] pairs ──
    // Reads the committed model, never the editor DOM (see header).
    function serializeGroup(card) {
        var fields = [];
        card.querySelectorAll('[data-chip-wrap]').forEach(function (wrap) {
            var key = wrap.getAttribute('data-key');
            if (!key) return;
            var value = committedValue(wrap);
            if (Array.isArray(value)) {
                value.forEach(function (v) { if (v !== '') fields.push(['af_' + key, String(v)]); });
            } else if (value !== '' && value != null) {
                fields.push(['af_' + key, String(value)]);
            }
        });
        return fields;
    }

    // ── Chip label from the editor's current DOM state (cheap, local) ──
    // Only ever called via commitChip → refreshChipLabel, i.e. on Save, so the
    // label can never describe an edit the operator abandoned.
    function chipDisplay(wrap) {
        var field = schemaOf(builderOf(wrap))[wrap.getAttribute('data-key')];
        var trigger = wrap.querySelector('[data-chip]');
        var editor = editorFor(wrap);
        if (!field || !trigger || !editor) return null;
        var type = trigger.getAttribute('data-type');
        if (type === 'int') {
            var num = editor.querySelector('input[data-edit]');
            var v = num ? num.value.trim() : '';
            if (v === '') return null;
            return intOp(field.key) + (field.prefix || '') + (field.no_thousands ? v : commas(v)) + (field.suffix ? ' ' + field.suffix : '');
        }
        if (type === 'bool') {
            var checked = editor.querySelector('input[type="radio"][data-edit]:checked');
            if (!checked) return null;               // unset → `Set…` + .chip-placeholder
            return checked.value === 'true' ? 'Yes' : 'No';
        }
        if (type === 'choice') {
            // Same radio read as bool; only the label mapping differs. The `Any`
            // row carries value="" → falsy → unset.
            var picked = editor.querySelector('input[type="radio"][data-edit]:checked');
            if (!picked || !picked.value) return null;
            var match = (field.choices || []).find(function (c) { return String(c[0]) === picked.value; });
            return match ? match[1] : picked.value;
        }
        var vals = [];                              // multichoice
        editor.querySelectorAll('input[type="checkbox"][data-edit]:checked').forEach(function (b) { vals.push(b.value); });
        if (!vals.length) return null;
        var labels = (field.choices || []).filter(function (c) { return vals.indexOf(String(c[0])) !== -1; })
            .map(function (c) { return c[1]; });
        return labels.join(', ');
    }

    function refreshChipLabel(wrap) {
        var disp = chipDisplay(wrap);
        var btn = wrap.querySelector('[data-chip]');
        var valEl = wrap.querySelector('[data-chip-value]');
        if (!btn || !valEl) return;
        valEl.textContent = disp === null ? 'Set…' : disp;
        btn.classList.toggle('chip-placeholder', disp === null);
        btn.classList.toggle('chip-muted', disp !== null);
    }

    // ── Campaign mode: per-group commit (POST → section swap) ──
    function commitGroup(card) {
        if (!card) return;
        var root = builderOf(card);
        if (window._cc && window._cc.closeAllDropdowns) window._cc.closeAllDropdowns();
        submitHxForm(card.getAttribute('data-update-url'), serializeGroup(card), root ? '#' + root.id : null);
    }

    // ── Blast mode: in-memory groups → hidden JSON field + recipient-count refresh ──
    function groupToObject(card) {
        var obj = {};
        serializeGroup(card).forEach(function (pair) {
            var k = pair[0], v = pair[1];
            if (Object.prototype.hasOwnProperty.call(obj, k)) {
                if (!Array.isArray(obj[k])) obj[k] = [obj[k]];
                obj[k].push(v);
            } else {
                obj[k] = v;
            }
        });
        return obj;
    }
    function serializeBlast(root) {
        var groups = [];
        var container = dnfGroupContainer(root);
        if (!container) return groups;
        container.querySelectorAll(':scope > [data-filter-group]').forEach(function (card) {
            var obj = groupToObject(card);
            if (Object.keys(obj).length) groups.push(obj);
        });
        return groups;
    }
    function syncBlast(root) {
        var hidden = dnfHiddenField(root);
        if (hidden) hidden.value = JSON.stringify(serializeBlast(root));
        // Announce the audience edit so an already-shown recipient estimate can be
        // marked stale (see the audience-count section). Deliberately NOT a re-query:
        // chip Save / Remove and group add/remove all land here, and the count is a
        // seconds-long live query that only an explicit button press may start.
        // The root travels in the detail because the listener needs the owning form,
        // and a portaled chip editor has no DOM path back to it.
        document.body.dispatchEvent(new CustomEvent('dnfChanged', { detail: { root: root } }));
    }
    function updateBlastDividers(root) {
        var container = dnfGroupContainer(root);
        if (!container) return;
        container.querySelectorAll(':scope > .divider').forEach(function (d) { d.remove(); });
        var cards = container.querySelectorAll(':scope > [data-filter-group]');
        cards.forEach(function (card, i) {
            if (i === 0) return;
            var div = document.createElement('div');
            div.className = 'divider';
            div.innerHTML = '<span class="divider__pill">OR</span>';
            container.insertBefore(div, card);
        });
    }
    // Add a group (optionally prefilled from a serialized {af_<key>: value} object).
    function addBlastGroup(root, prefill) {
        var tmpl = root.querySelector('template[data-dnf-group-template]')
            || root.querySelector('template[data-blast-group-template]');
        var container = dnfGroupContainer(root);
        if (!tmpl || !container) return null;
        var card = tmpl.content.cloneNode(true).querySelector('[data-filter-group]');
        if (!card) return null;
        var gid = 'bg' + (++blastGroupSeq);
        card.setAttribute('data-cf', gid);
        card.querySelectorAll('[data-add-field]').forEach(function (a) { a.setAttribute('data-cf', gid); });
        container.appendChild(card);

        if (prefill && typeof prefill === 'object') {
            var bar = card.querySelector('.chip-bar');
            var picker = bar ? bar.querySelector('.dropdown:not([data-chip-wrap])') : null;
            Object.keys(prefill).forEach(function (afKey) {
                var key = afKey.indexOf('af_') === 0 ? afKey.slice(3) : afKey;
                if (!bar) return;
                // Template existence IS the validity check — no schema lookup needed.
                var chip = buildChip(root, gid, key);
                if (!chip) return;
                bar.insertBefore(chip, picker || null);
                writeEditor(chip, prefill[afKey]);
                commitChip(chip);        // seeds _cfValue + paints the label
            });
        }
        updateBlastDividers(root);
        return card;
    }
    function initBlastBuilders() {
        document.querySelectorAll('[data-filter-builder="blast"], [data-filter-builder="template"]').forEach(function (root) {
            if (root.__hydrated) return;
            root.__hydrated = true;
            var hidden = dnfHiddenField(root);
            var raw = hidden ? (hidden.value || '').trim() : '';
            if (raw) {
                try {
                    var arr = JSON.parse(raw);
                    if (Array.isArray(arr)) {
                        arr.forEach(function (obj) {
                            if (obj && typeof obj === 'object') addBlastGroup(root, obj);
                        });
                    }
                } catch (e) { /* ignore malformed echo */ }
            }
            if (hidden) hidden.value = JSON.stringify(serializeBlast(root));
            // No `dnfChanged` here: hydration replays what the operator already had,
            // it is not an edit, and the freshly rendered card carries no estimate to
            // mark stale.
        });
    }

    // ── Manual audience counts (campaign create sheet + blast create sheet) ──
    // Both sheets host an expensive count the operator starts by hand, so both use
    // ONE contract instead of two mechanisms:
    //   * the card is `[data-audience-count-card]` and carries
    //     `data-audience-count-state` + `data-audience-count-revision`;
    //   * the trigger is `[data-audience-count-button]` inside it;
    //   * the form whose inputs the count reads is the one the button's `hx-include`
    //     names — that attribute IS the association, so no card names its form twice.
    // A card declares only the `[data-audience-count-status]` regions it actually has, and a
    // region's value is a space-separated list of the states it's visible in — most regions
    // declare exactly one (idle/loading/stale/error), but a region may opt into more than one
    // state. The blast card's figures region declares `"result stale"`: last-known-good figures
    // stay on screen, at full contrast, beside the stale warning once targeting changes after a
    // calculation — hiding them would throw away a still-useful sense of scale for no reason.
    // `loading` still hides them for the query's duration; that region declares only `"loading"`.
    // `[data-audience-count-label]` is likewise optional — the blast button's label is server-
    // driven (`_recipient_count.html` OOB-swaps it to "Calculate again"), not this hook.
    //
    // Two swap shapes are supported. Campaign: the button targets the CARD, and the
    // server re-renders it with the next state baked in. Blast: the button targets a
    // region INSIDE the card, so the server never sees the card at all and the state
    // is flipped to `result` here on afterSwap.
    //
    // The card is its own HTMX target, so targeting inputs stay usable while the
    // request runs; the revision counter stops a slow in-flight count from painting
    // over a `stale` the operator earned by editing targeting while it ran.
    var audienceCountRequestRevisions = new WeakMap();

    function countCardOf(el) {
        return el && el.closest ? el.closest('[data-audience-count-card]') : null;
    }
    function countCardOfTrigger(el) {
        return countCardOf(el && el.closest ? el.closest('[data-audience-count-button]') : null);
    }
    // Cards this form feeds. A card whose button has no hx-include (the saved
    // campaign targeting card on the detail page) belongs to no form and is never
    // marked stale from an edit — same reach the hardcoded id check had.
    function countCardsForForm(form) {
        var cards = [];
        if (!form || !form.id) return cards;
        var buttons = document.querySelectorAll(
            '[data-audience-count-button][hx-include="#' + form.id + '"]');
        buttons.forEach(function (button) {
            var card = countCardOf(button);
            if (card && cards.indexOf(card) === -1) cards.push(card);
        });
        return cards;
    }
    // A region is visible when `state` appears in its space-separated status-token list — see
    // the header comment above for why the blast card's figures region needs more than one.
    function regionVisibleForState(region, state) {
        var tokens = (region.getAttribute('data-audience-count-status') || '').split(/\s+/);
        return tokens.indexOf(state) !== -1;
    }
    function setAudienceCountState(card, state) {
        if (!card) return;
        card.setAttribute('data-audience-count-state', state);
        card.querySelectorAll('[data-audience-count-status]').forEach(function (region) {
            region.hidden = !regionVisibleForState(region, state);
        });
        var label = card.querySelector('[data-audience-count-label]');
        if (label) {
            label.textContent = state === 'result' || state === 'stale'
                ? 'Recalculate audience' : 'Calculate audience';
        }
    }
    function markAudienceCountStale(form) {
        countCardsForForm(form).forEach(function (card) {
            var state = card.getAttribute('data-audience-count-state');
            // `idle` means nothing has been counted yet — there is nothing to invalidate.
            if (state === 'idle') return;
            // Always fence the revision, even for a first count that is still `loading`
            // and has no figures yet — this is what stops that in-flight response from
            // landing as current once htmx:beforeSwap compares revisions below.
            var revision = parseInt(card.getAttribute('data-audience-count-revision'), 10) || 0;
            card.setAttribute('data-audience-count-revision', String(revision + 1));
            // `loading` with no prior result means this edit landed during the FIRST
            // count, before any figures ever existed — there is nothing to show as
            // stale, so return to idle instead of a `stale` state with an empty figures
            // region. `_audienceCountHasResult` is captured in the htmx:beforeRequest
            // handler from the state THIS request is overwriting to `loading`, since by
            // the time we get here that evidence is already gone from the attribute.
            var neverCounted = state === 'loading' && !card._audienceCountHasResult;
            setAudienceCountState(card, neverCounted ? 'idle' : 'stale');
        });
    }

    document.addEventListener('input', function (e) {
        markAudienceCountStale(e.target.closest && e.target.closest('form'));
    });
    document.addEventListener('change', function (e) {
        markAudienceCountStale(e.target.closest && e.target.closest('form'));
    });
    // DNF chip edits never surface as a form `change`: nothing commits until Save,
    // and a portaled multichoice editor has no closest('form') to begin with. That is
    // what `dnfChanged` is for — the audience changed, mark the estimate stale.
    document.body.addEventListener('dnfChanged', function (e) {
        var root = e.detail && e.detail.root;
        markAudienceCountStale(root && root.closest ? root.closest('form') : null);
    });
    document.body.addEventListener('htmx:configRequest', function (e) {
        var card = countCardOfTrigger(e.detail.elt);
        if (!card) return;
        e.detail.parameters.audience_count_revision =
            card.getAttribute('data-audience-count-revision') || '0';
    });
    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var card = countCardOfTrigger(e.detail.elt);
        if (!card) return;
        if (e.detail.xhr) {
            var parameters = e.detail.requestConfig && e.detail.requestConfig.parameters;
            audienceCountRequestRevisions.set(
                e.detail.xhr,
                String(parameters ? parameters.audience_count_revision : '')
            );
        }
        // Remember that this card has shown real figures at least once, captured from
        // the state THIS request is about to overwrite to `loading` — markAudienceCountStale
        // needs that memory while state reads `loading` mid-request (see there). `stale`
        // counts too: it can only be reached from a prior `result` (or, for the saved
        // standing-list card, a server-detected concurrent edit on a real snapshot).
        var priorState = card.getAttribute('data-audience-count-state');
        if (priorState === 'result' || priorState === 'stale') card._audienceCountHasResult = true;
        setAudienceCountState(card, 'loading');
    });
    // Keyed off the recorded xhr, so only a count request is guarded — never some
    // unrelated swap that happens to target something inside the card.
    document.body.addEventListener('htmx:beforeSwap', function (e) {
        if (!e.detail.xhr || !audienceCountRequestRevisions.has(e.detail.xhr)) return;
        var card = countCardOf(e.detail.target);
        if (!card) return;
        if (audienceCountRequestRevisions.get(e.detail.xhr)
            !== card.getAttribute('data-audience-count-revision')) {
            e.detail.shouldSwap = false;
        }
    });
    document.body.addEventListener('htmx:afterSwap', function (e) {
        if (!e.detail.xhr || !audienceCountRequestRevisions.has(e.detail.xhr)) return;
        var card = countCardOf(e.detail.target);
        // Whole-card swap: the server-rendered replacement already carries its state.
        // Region swap: the card itself survived, still reading `loading`.
        if (card && card !== e.detail.target) setAudienceCountState(card, 'result');
    });

    // ── Cancel-by-default: restore the editor from the model on (re)open ──
    // There is no close event to hook — every close path funnels into dropdowns.js's
    // private closeMenu() and announces nothing. So discard is handled on the NEXT
    // open, which covers Escape and click-away for free. `dropdown:open` bubbles and
    // is dispatched on the TRIGGER, which is never portaled — closest() is correct
    // here specifically (everywhere else needs the portal-aware wrapFor).
    document.addEventListener('dropdown:open', function (e) {
        var wrap = e.target.closest && e.target.closest('[data-chip-wrap]');
        if (!wrap || !builderOf(wrap)) return;
        if (wrap._cfValue === undefined) wrap._cfValue = readEditor(wrap);  // seed from untouched DOM
        else revertChip(wrap);                                             // drop a cancelled edit
    });

    // ── Enter inside a chip editor = Save ──
    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' || !e.target.closest) return;
        if (e.target.tagName === 'TEXTAREA') return;
        if (!e.target.closest('[data-edit]') || !e.target.closest('[data-editor]')) return;
        var save = e.target.closest('[data-editor]').querySelector('[data-chip-save]');
        if (!save) return;
        e.preventDefault();
        save.click();
    });

    // ── click delegation ──
    document.addEventListener('click', function (e) {
        var t = e.target;
        if (!t.closest) return;

        // Save — the ONLY commit. Freeze the model BEFORE closing: closeAll()
        // restores a portaled menu, and a frozen model makes the close path inert.
        // Deliberately NOT [data-dropdown-close] — this ordering is the point.
        var sv = t.closest('[data-chip-save]');
        if (sv) {
            var svWrap = wrapFor(sv);
            if (!svWrap) return;
            var svRoot = builderOf(svWrap);
            if (!svRoot) return;
            var svCard = groupCardOf(svWrap);
            commitChip(svWrap);
            if (window._cc && window._cc.closeAllDropdowns) window._cc.closeAllDropdowns();
            if (usesSerializedDnf(svRoot)) syncBlast(svRoot);
            else commitGroup(svCard);
            return;
        }

        // Cancel — revert eagerly so the discard is immediate; the closing itself
        // is SeeMe's [data-dropdown-close]. (The dropdown:open restore above still
        // backstops Escape / click-away.)
        var cn = t.closest('[data-chip-cancel]');
        if (cn) {
            var cnWrap = wrapFor(cn);
            if (cnWrap && builderOf(cnWrap)) revertChip(cnWrap);
            return;
        }

        // Remove filter — the button lives inside the chip editor, which for
        // multichoice is portaled to body. Resolve the wrap by stamped identity.
        var rm = t.closest('[data-remove-condition]');
        if (rm) {
            var rmWrap = wrapFor(rm);
            if (!rmWrap) return;
            var rmRoot = builderOf(rmWrap);
            if (!rmRoot) return;
            var rmCard = groupCardOf(rmWrap);
            if (window._cc && window._cc.closeAllDropdowns) window._cc.closeAllDropdowns();
            rmWrap.remove();
            if (usesSerializedDnf(rmRoot)) syncBlast(rmRoot);
            else commitGroup(rmCard);      // remove is a commit boundary — flush immediately
            return;
        }

        // Remaining actions live in non-portaled menus/buttons within a builder.
        var root = builderOf(t);
        if (!root) return;
        var blast = usesSerializedDnf(root);

        // Blast: add a new empty group.
        var addg = t.closest('[data-add-blast-group]');
        if (addg) {
            e.preventDefault();
            addBlastGroup(root, null);
            return;
        }

        // Blast: remove a whole group.
        var rmg = t.closest('[data-remove-blast-group]');
        if (rmg) {
            e.preventDefault();
            var gcardDel = rmg.closest('[data-filter-group]');
            if (window._cc && window._cc.closeAllDropdowns) window._cc.closeAllDropdowns();
            if (gcardDel) gcardDel.remove();
            updateBlastDividers(root);
            syncBlast(root);
            return;
        }

        var add = t.closest('[data-add-field]');
        if (add) {
            e.preventDefault();
            var cf = add.getAttribute('data-cf');
            var key = add.getAttribute('data-add-field');
            var gcard = groupCardOf(add);
            var bar = gcard && gcard.querySelector('.chip-bar');
            if (!bar) return;
            // Blast: the picker keeps every field, so guard against a duplicate chip
            // (a 2nd chip for the same key would serialize into a bogus array). Just
            // open the existing one instead.
            if (blast) {
                var existing = gcard.querySelector('[data-chip-wrap][data-key="' + key + '"]');
                if (existing) {
                    var et = existing.querySelector('[data-chip]');
                    if (et) setTimeout(function () { et.click(); }, 0);
                    return;
                }
            }
            var chip = buildChip(root, cf, key);
            if (!chip) return;
            var picker = bar.querySelector('.dropdown:not([data-chip-wrap])');
            bar.insertBefore(chip, picker || null);
            chip._cfValue = readEditor(chip);   // unset — a new chip commits nothing
            if (!blast) add.remove();   // campaign re-renders the picker server-side
            setTimeout(function () {
                var trig = chip.querySelector('[data-chip]');
                if (trig) trig.click();
            }, 0);
            return;
        }

        // Campaign-only: focus the inline rename editor.
        var fr = t.closest('[data-focus-rename]');
        if (fr) {
            e.preventDefault();
            var rcf = fr.getAttribute('data-cf');
            setTimeout(function () {
                var span = root.querySelector('[data-rename][data-cf="' + rcf + '"]');
                if (span && window.SeeMe && window.SeeMe.inlineEdit) window.SeeMe.inlineEdit.start(span);
            }, 0);
            return;
        }
    });

    // ── Campaign group rename — inline-edit commits via bubbling custom event ──
    document.addEventListener('inline-edit:commit', function (e) {
        var span = e.target.closest && e.target.closest('[data-rename]');
        if (!span) return;
        var root = builderOf(span);
        if (!root || modeOf(root) !== 'campaign') return;
        var url = span.getAttribute('data-rename-url');
        if (url) submitHxForm(url, [['name', e.detail.value]], '#' + root.id);
    });

    // ── SortableJS group reorder — campaign only (#groups is replaced on each swap) ──
    var sortable = null;
    var boundGroups = null;
    function initSortable() {
        var groups = document.getElementById('groups');
        if (groups === boundGroups) return; // already bound to this element
        if (sortable) { try { sortable.destroy(); } catch (e) { /* noop */ } sortable = null; }
        boundGroups = groups;
        if (!groups || !groups.querySelector('.drag-handle')) return; // read-only view → no drag
        sortable = Sortable.create(groups, {
            draggable: '.card[data-cf]',
            handle: '.drag-handle',
            animation: 150,
            onEnd: function () {
                var fields = [];
                groups.querySelectorAll('.card[data-cf]').forEach(function (c) {
                    fields.push(['order', c.getAttribute('data-cf')]);
                });
                var root = groups.closest(BUILDER_SEL);
                submitHxForm(groups.getAttribute('data-reorder-url'), fields, root ? '#' + root.id : null);
            }
        });
    }

    // Exposed so the blast create form's inline script can (re)hydrate on sheet open.
    window.__filterBuilderInitBlast = initBlastBuilders;

    function boot() { initSortable(); initBlastBuilders(); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.body.addEventListener('htmx:afterSwap', function () { initSortable(); initBlastBuilders(); });

})();
